/**
 * Product taxonomy for tools. Every tool belongs to exactly one group and has a
 * maturity label. Legacy categories map onto groups so older data keeps working.
 */
import type { Category } from "@/components/ds/PageHero";

export const TOOL_GROUPS = ["Create", "Build", "Security", "Productivity", "Education"] as const;
export type ToolGroup = (typeof TOOL_GROUPS)[number];

export const GROUP_INFO: Record<ToolGroup, { description: string; accent: Category }> = {
  Create: { description: "Write, design and make media", accent: "creative" },
  Build: { description: "Chatbots, workflows, code and automations", accent: "prompting" },
  Security: { description: "Protect data and analyze risk", accent: "cyber" },
  Productivity: { description: "Everyday tasks, money and planning", accent: "video" },
  Education: { description: "Study help and career growth", accent: "fluency" },
};

const LEGACY_TO_GROUP: Record<string, ToolGroup> = {
  Creative: "Create",
  Developer: "Build",
  AI: "Build",
  Security: "Security",
  Finance: "Productivity",
  Productivity: "Productivity",
  Lifestyle: "Productivity",
  Education: "Education",
  Career: "Education",
};

/** Per-tool overrides where the legacy category is misleading. */
const GROUP_OVERRIDES: Record<string, ToolGroup> = {
  "cyber-kitchen": "Security",
  "contract-risk-analyzer": "Security",
  "skill-builder": "Build",
};

export const groupForTool = (id: string, legacyCategory: string): ToolGroup =>
  GROUP_OVERRIDES[id] ?? LEGACY_TO_GROUP[legacyCategory] ?? "Productivity";

export type Maturity = "Stable" | "Beta" | "Experimental" | "Needs setup";

/** Anything not listed is Stable. Keep this honest: label what isn't finished. */
const MATURITY: Record<string, Maturity> = {
  "creative-director": "Beta",
  "storyboard-video-studio": "Beta",
  "character-replacement": "Needs setup",
  "ai-video-generator": "Experimental",
  "skill-builder": "Beta",
  "geniebot-workflow": "Beta",
};

export const maturityFor = (id: string): Maturity => MATURITY[id] ?? "Stable";

export const isToolGroup = (v: string | null): v is ToolGroup =>
  !!v && (TOOL_GROUPS as readonly string[]).includes(v);
